import * as XLSX from 'xlsx';
import { readExamImportFile } from './examAssignmentImport';

export const SCORE_IMPORT_HEADERS = [
    '分配ID(勿改)',
    '姓名(勿改)',
    '身份证号(勿改)',
    '准考证号(勿改)',
    '报考专业(勿改)',
    '科目安排ID(勿改)',
    '考试科目(勿改)',
    '分数',
];

const INSTRUCTION_ROWS = [
    ['成绩导入模板 - 填写说明'],
    [''],
    ['1. 请勿修改「分配ID」「姓名」「身份证号」「准考证号」「报考专业」「科目安排ID」「考试科目」七列，系统据此匹配考生与科目。'],
    ['2. 同一考生有多门科目时：每门科目占一行，前七列保持与模板一致，在「分数」列填写该科成绩。'],
    ['3. 「分数」为必填，支持整数或小数；同一考生所有科目均需填写分数后才会导入。'],
    ['4. 仅「待录入」状态考生可导入；导入确认后将写入系统并变为「已录入」。'],
    [''],
    ['请在「成绩录入」工作表中填写数据。'],
];

const EXPORT_INSTRUCTION_ROWS = [
    ['已录入考生成绩导出'],
    [''],
    ['本文件列结构与「导入模板」一致，可直接用于备份或二次核对。'],
    ['同一考生多门科目为多行数据；前七列请勿修改。'],
];

function cellText(value) {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value).trim();
}

function parseScore(value) {
    const text = cellText(value);
    if (!text) {
        return null;
    }
    const num = Number(text);
    if (Number.isNaN(num)) {
        return null;
    }
    return num;
}

function formatExportFileTime() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

function buildScoreWorkbook(dataRows, instructionRows) {
    const instructionSheet = XLSX.utils.aoa_to_sheet(instructionRows);
    const dataSheet = XLSX.utils.aoa_to_sheet([
        SCORE_IMPORT_HEADERS,
        ...dataRows,
    ]);

    instructionSheet['!cols'] = [{ wch: 88 }];
    dataSheet['!cols'] = [
        { wch: 12 }, { wch: 10 }, { wch: 20 }, { wch: 16 }, { wch: 24 },
        { wch: 16 }, { wch: 16 }, { wch: 10 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, instructionSheet, '填写说明');
    XLSX.utils.book_append_sheet(wb, dataSheet, '成绩录入');
    return wb;
}

function buildScoreRows(students, withScores) {
    const rows = [];
    (students || []).forEach((item) => {
        const schedules = item.examScheduleList || [];
        const base = [
            item.id,
            item.applicantName || '',
            item.idCardNumber || '',
            item.ticketNumber || '',
            item.positionName || '',
        ];
        if (!schedules.length) {
            rows.push([...base, '', '', '']);
            return;
        }
        schedules.forEach((subject) => {
            const scoreValue = withScores && subject.score !== null && subject.score !== undefined && subject.score !== ''
                ? subject.score
                : '';
            rows.push([
                ...base,
                subject.id || '',
                subject.examSubject || '',
                scoreValue,
            ]);
        });
    });
    return rows;
}

export function downloadScoreImportTemplate(pendingStudents) {
    const list = Array.isArray(pendingStudents) ? pendingStudents : [];
    const dataRows = buildScoreRows(list, false);
    const wb = buildScoreWorkbook(dataRows, INSTRUCTION_ROWS);
    XLSX.writeFile(wb, `成绩导入模板-${formatExportFileTime()}.xlsx`);
}

export function downloadScoredExport(scoredStudents) {
    const dataRows = buildScoreRows(scoredStudents, true);
    const wb = buildScoreWorkbook(dataRows, EXPORT_INSTRUCTION_ROWS);
    XLSX.writeFile(wb, `已录入成绩-${formatExportFileTime()}.xlsx`);
}

function findDataSheet(workbook) {
    if (workbook.SheetNames.indexOf('成绩录入') !== -1) {
        return workbook.Sheets['成绩录入'];
    }
    return workbook.Sheets[workbook.SheetNames[0]];
}

function rowToObject(row) {
    const obj = {};
    SCORE_IMPORT_HEADERS.forEach((header, index) => {
        obj[header] = row[index];
    });
    return obj;
}

function isHeaderRow(row) {
    if (!row || !row.length) {
        return false;
    }
    const first = cellText(row[0]);
    return first.indexOf('分配ID') !== -1 || first === '分配ID(勿改)';
}

function isEmptyDataRow(row) {
    if (!row || !row.length) {
        return true;
    }
    return SCORE_IMPORT_HEADERS.every((_header, index) => cellText(row[index]) === '');
}

function buildPendingScheduleMap(pendingStudents) {
    const map = {};
    (pendingStudents || []).forEach((item) => {
        const scheduleMap = {};
        (item.examScheduleList || []).forEach((schedule) => {
            scheduleMap[String(schedule.id)] = schedule;
        });
        map[String(item.id)] = {
            ...item,
            scheduleMap,
        };
    });
    return map;
}

export function parseScoreImportWorkbook(workbook, pendingStudents) {
    const pendingMap = buildPendingScheduleMap(pendingStudents);
    const sheet = findDataSheet(workbook);
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    const parsedRows = [];
    const errors = [];

    rows.forEach((row, rowIndex) => {
        if (isHeaderRow(row) || isEmptyDataRow(row)) {
            return;
        }
        const item = rowToObject(row);
        const assignmentIdText = cellText(item['分配ID(勿改)']);
        const scheduleIdText = cellText(item['科目安排ID(勿改)']);
        if (!assignmentIdText) {
            errors.push(`第 ${rowIndex + 1} 行：分配ID不能为空`);
            return;
        }
        if (!scheduleIdText) {
            errors.push(`第 ${rowIndex + 1} 行：科目安排ID不能为空`);
            return;
        }
        parsedRows.push({
            rowIndex: rowIndex + 1,
            assignmentId: assignmentIdText,
            scheduleId: scheduleIdText,
            applicantName: cellText(item['姓名(勿改)']),
            idCardNumber: cellText(item['身份证号(勿改)']),
            ticketNumber: cellText(item['准考证号(勿改)']),
            positionName: cellText(item['报考专业(勿改)']),
            examSubject: cellText(item['考试科目(勿改)']),
            score: parseScore(item['分数']),
            pending: pendingMap[assignmentIdText] || null,
            schedule: pendingMap[assignmentIdText]
                ? pendingMap[assignmentIdText].scheduleMap[scheduleIdText] || null
                : null,
        });
    });

    if (!parsedRows.length && !errors.length) {
        errors.push('未读取到有效数据，请使用系统下载的模板填写');
    }

    const groupedMap = {};
    parsedRows.forEach((row) => {
        if (!groupedMap[row.assignmentId]) {
            groupedMap[row.assignmentId] = {
                examAssignmentId: Number(row.assignmentId),
                applicantName: row.applicantName,
                idCardNumber: row.idCardNumber,
                ticketNumber: row.ticketNumber,
                positionName: row.positionName,
                scoresList: [],
                rowIndexes: [],
                pending: row.pending,
            };
        }
        const group = groupedMap[row.assignmentId];
        group.rowIndexes.push(row.rowIndex);
        group.scoresList.push({
            examScheduleId: Number(row.scheduleId),
            examSubject: row.examSubject,
            score: row.score,
            rowIndex: row.rowIndex,
            schedule: row.schedule,
        });
    });

    const previewList = Object.keys(groupedMap).map((key) => groupedMap[key]);

    previewList.forEach((group) => {
        const label = group.applicantName || `分配ID=${group.examAssignmentId}`;
        if (!group.pending) {
            errors.push(`${label}：分配ID不在当前待录入列表中`);
            return;
        }
        if (group.pending.statusScore !== 0) {
            errors.push(`${label}：该考生不是待录入状态`);
        }
        if (group.applicantName && group.pending.applicantName
            && group.applicantName !== group.pending.applicantName) {
            errors.push(`${label}：姓名与系统不一致，请勿修改模板`);
        }
        if (group.idCardNumber && group.pending.idCardNumber
            && group.idCardNumber.toUpperCase() !== String(group.pending.idCardNumber).toUpperCase()) {
            errors.push(`${label}：身份证号与系统不一致，请勿修改模板`);
        }
        if (!group.scoresList.length) {
            errors.push(`${label}：至少填写一门科目分数`);
        }

        const expectedCount = (group.pending.examScheduleList || []).length;
        if (expectedCount && group.scoresList.length !== expectedCount) {
            errors.push(`${label}：应填写 ${expectedCount} 门科目分数，当前 ${group.scoresList.length} 行`);
        }

        const seenScheduleIds = new Set();
        group.scoresList.forEach((subject) => {
            if (!subject.schedule) {
                errors.push(`${label} 第 ${subject.rowIndex} 行：科目安排ID不在系统中`);
            } else if (subject.examSubject && subject.schedule.examSubject
                && subject.examSubject !== subject.schedule.examSubject) {
                errors.push(`${label} 第 ${subject.rowIndex} 行：考试科目与系统不一致，请勿修改模板`);
            }
            if (seenScheduleIds.has(subject.examScheduleId)) {
                errors.push(`${label} 第 ${subject.rowIndex} 行：科目安排ID重复`);
            }
            seenScheduleIds.add(subject.examScheduleId);
            if (subject.score === null || subject.score === undefined) {
                errors.push(`${label} 第 ${subject.rowIndex} 行：分数不能为空且须为数字`);
            }
        });
    });

    return {
        previewList,
        errors: Array.from(new Set(errors)),
        payload: previewList.map((group) => {
            let totalScore = 0;
            const scoresList = group.scoresList.map((subject) => {
                totalScore += subject.score;
                return {
                    examScheduleId: subject.examScheduleId,
                    score: subject.score,
                };
            });
            return {
                examAssignmentId: group.examAssignmentId,
                applicantName: group.applicantName,
                idCardNumber: group.idCardNumber,
                totalScore,
                scoresList,
            };
        }),
    };
}

export { readExamImportFile as readScoreImportFile };
